import { useEffect, useState } from 'react';
import type { Nullable } from 'corgi-types';

import { useLocalization } from 'common/providers/LocaleProvider';
import type { DispatchState } from 'common/types/React';
import type { DrawerAlert } from './alert';

interface FetcherResponse<T> {
  currentId: Nullable<string>;
  content: T;
}

type Fetcher<T> = (reclamationId: string, itemId: string) => Promise<FetcherResponse<T>>;

interface UseFetchContentOptions<T> {
  reclamationId: string;
  itemId: string;
  fetcher: Fetcher<T>;
  setAlert: DispatchState<DrawerAlert>;
}

export const useFetchStepContent = <T>({
  reclamationId,
  itemId,
  fetcher,
  setAlert,
}: UseFetchContentOptions<T>) => {
  const [content, setContent] = useState<Nullable<T>>(null);
  const [id, setId] = useState<Nullable<string>>(null);
  const { t } = useLocalization();

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const { currentId, content: stepContent } = await fetcher(reclamationId, itemId);

        if (isMounted) {
          setId(currentId);
          setContent(stepContent);
        }
      } catch {
        if (isMounted) {
          setAlert({ message: t('h24_something_went_wrong') });
        }
      }
    })();

    return () => {
      isMounted = false;
      setContent(null);
      setId(null);
    };
  }, [reclamationId, itemId]);

  return { content, id, setId };
};

export const useObserveAlert = (alert: DrawerAlert, setAlert: DispatchState<DrawerAlert>) => {
  useEffect(() => {
    setAlert(alert);
  }, [alert]);
};
